// src/pages/Login.tsx
//
// Login page. ONE sign-in action: clicking the button kicks off the
// Keycloak authorization-code flow. We don't list SSO providers, we
// don't list demo accounts, and we don't offer any other path.
//
// After login, Keycloak redirects back here. We then send the
// user to the right place for their account type, or honour the
// ?from= query param if it points to a safe internal path.

import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { LogIn, ArrowRight, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/Button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { useAuth } from "@/components/auth/AuthContext";
import { inventoryCapabilities } from "@/lib/inventory/capabilities";

/** Map account type -> default landing page (signed in). */
function defaultRouteFor(accountType: string): string {
  switch (accountType) {
    case "admin":
      return "/admin";
    case "demo":
      return "/admin/dashboard";
    case "customer":
      return "/portal";
    case "sales":
    case "marketing-sales":
      return "/admin/sales";
    case "legal":
    case "management":
      return "/admin/incentives";
    case "auditor":
      return "/admin";
    default:
      return "/";
  }
}

function safeFromPath(raw: string | null): string {
  if (!raw) return "";
  if (!raw.startsWith("/") || raw.startsWith("//")) return "";
  // This value also reaches auth.login -> the BFF next parameter. Do not pass
  // browser-normalized backslash or control-character variants as return paths.
  if (/[\\\u0000-\u001f\u007f]/.test(raw)) return "";
  return raw;
}

export function Login() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const auth = useAuth();
  const [searchParams] = useSearchParams();
  const fromPath = safeFromPath(searchParams.get("from"));

  // As soon as we know the user is authenticated, push them to the
  // right place. Don't make them click again.
  useEffect(() => {
    if (!auth.isReady || !auth.isAuthenticated) return;
    const inventoryOnly =
      inventoryCapabilities(auth.groups).read &&
      (auth.accountType === "anonymous" || auth.accountType === "demo");
    const fallback = inventoryOnly
      ? "/admin/inventory"
      : defaultRouteFor(auth.accountType);
    const dest = fromPath || fallback;
    navigate(dest, { replace: true });
  }, [
    auth.isReady,
    auth.isAuthenticated,
    auth.accountType,
    auth.groups,
    fromPath,
    navigate,
  ]);

  if (!auth.isReady) {
    return (
      <div className="container flex min-h-[60vh] items-center justify-center py-20">
        <div className="text-center">
          <Loader2 className="mx-auto h-7 w-7 animate-spin text-accent-indigo" />
          <p className="mt-3 text-sm text-fg-tertiary">
            {t("auth.signingIn", "Signing in…")}
          </p>
        </div>
      </div>
    );
  }

  // Anonymous: a single sign-in area, no SSO provider list, no
  // demo accounts, no other options. Click -> Keycloak.
  return (
    <div className="container max-w-md py-24">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-xl">
            <LogIn className="h-5 w-5 text-accent-indigo" strokeWidth={1.5} />
            {t("auth.signIn", "Sign in")}
          </CardTitle>
          <CardDescription>
            {t(
              "auth.signInBody",
              "Sign in with your BENCHMARK account to continue. You'll be redirected to the identity provider, then sent back here.",
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button
            onClick={() => {
              // Carry the from-path through the full-page BFF
              // redirect so the user lands where they were headed.
              auth.login(fromPath || undefined);
            }}
            size="lg"
            className="w-full"
          >
            <LogIn className="h-4 w-4" strokeWidth={1.5} />
            {t("auth.continue", "Continue with BENCHMARK")}
            <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
          </Button>
        </CardContent>
      </Card>

      <div className="mt-8">
        <Badge variant="default" size="sm" className="mb-2">
          {t("auth.justLooking", "Just looking?")}
        </Badge>
        <p className="text-xs text-fg-tertiary">
          {t(
            "auth.justLookingBody",
            "The catalog and store pages are public — no account needed.",
          )}{" "}
          <Link to="/catalog" className="text-accent-indigo hover:underline">
            {t("nav.catalog", "Catalog")} →
          </Link>
        </p>
      </div>
    </div>
  );
}
